import type { ReactNode } from 'react';
import { Button } from '../primitives/Button.js';
import { Group } from '../primitives/Group.js';
import { Text } from '../primitives/Text.js';
import { StatusPage } from './StatusPage.js';

export type AppErrorFallbackProps = {
  traceId?: string | null;
  onRetry?: () => void;
  /** Leave broken route (full navigation — breaks retry loops on crashy pages). */
  onGoHome?: () => void;
  homeLabel?: string;
};

export function AppErrorFallback({
  traceId,
  onRetry,
  onGoHome,
  homeLabel = "Retour à l'accueil",
}: AppErrorFallbackProps) {
  const canCopy = Boolean(traceId?.trim());
  const handleCopy = async () => {
    const v = traceId?.trim();
    if (!v) return;
    try {
      await window.navigator.clipboard.writeText(v);
    } catch {
      try {
        const el = document.createElement('textarea');
        el.value = v;
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      } catch {
        // ignore
      }
    }
  };

  const description: ReactNode = (
    <>
      <Text c="muted">
        L&apos;incident a été enregistré automatiquement. Revenez à l&apos;accueil pour continuer.
      </Text>
      {traceId ? (
        <Text size="sm" c="muted">
          Code erreur : <code>{traceId}</code>
        </Text>
      ) : null}
    </>
  );

  return (
    <StatusPage variant="error" title="Une erreur est survenue" description={description}>
      <Group justify="center" gap="sm" wrap="wrap">
        {onGoHome ? (
          <Button type="button" onClick={onGoHome}>
            {homeLabel}
          </Button>
        ) : null}
        {onRetry ? (
          <Button type="button" variant="soft" onClick={onRetry}>
            Réessayer
          </Button>
        ) : null}
        {canCopy ? (
          <Button type="button" variant="ghost" onClick={handleCopy}>
            Copier code erreur
          </Button>
        ) : null}
      </Group>
    </StatusPage>
  );
}
